import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { Loading } from "@/Loading";
import { useTicketList } from "~/services/customer/myPage";
import { TicketListItem } from "~/types/common";
import { useMemo } from "react";
import { Tabs } from "@/Tabs";
import { useTab } from "^/common/useTab";

export const Route = createFileRoute("/customer/layout/user-subscriptions")({
  component: UserSubscriptions,
});

interface UserSubscription {
  subscriptionSeq: number;
  planName: string;
  companyName: string;
  companySeq: number;
  purchaseDate: string;
  usableDate: string;
  expiryDate: string | null;
  /** 최초 이용일 방식: 첫 이용 전이면 만료일 대신 "첫 이용일로부터 N일" 안내 */
  usageDurationDays: number | null;
  isFirstUsePending: boolean;
  isCountBased: boolean;
  totalCount: number | null;
  remainingCount: number | null;
  cdProductType?: string;
  status: "active" | "expired" | "used";
}

function UserSubscriptions() {
  const navigate = useNavigate();
  const { tab, handleTab, tabList } = useTab(["사용 가능", "사용 불가능"]);
  const isUseable = tab === "사용 가능";
  const { data: ticketList = [], isLoading } = useTicketList(isUseable);

  // API 응답 데이터를 UserSubscription 타입으로 변환
  const subscriptions: UserSubscription[] = useMemo(() => {
    if (!ticketList || ticketList.length === 0) {
      return [];
    }

    return ticketList.map((ticket: TicketListItem) => {
      // 만료일 확인하여 상태 결정
      let status: "active" | "expired" | "used" = "active";
      const now = new Date();
      // 오늘 날짜의 시작 시간으로 설정 (00:00:00)
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      const expiryDate = ticket.expiredDate
        ? new Date(ticket.expiredDate)
        : null;

      if (expiryDate) {
        // 만료일의 날짜만 비교 (시간 제외)
        const expiryDateOnly = new Date(
          expiryDate.getFullYear(),
          expiryDate.getMonth(),
          expiryDate.getDate()
        );
        // 만료일이 오늘보다 과거인 경우에만 만료됨 (오늘이 만료일이면 사용 가능)
        if (expiryDateOnly < today) {
          status = "expired";
        } else if (ticket.remainCount === 0) {
          // remainCount가 0이면 사용 완료 (splitYn과 관계없이)
          status = "used";
        }
      } else if (ticket.remainCount === 0) {
        // remainCount가 0이면 사용 완료 (splitYn과 관계없이)
        status = "used";
      }

      return {
        subscriptionSeq: ticket.purchaseTicketSeq,
        planName: ticket.name,
        companyName: ticket.companyName,
        companySeq: ticket.companySeq,
        purchaseDate: ticket.purchaseDate,
        // 최초 이용일 방식으로 시작된 티켓은 발급일이 아닌 첫 방문일을 시작일로 표시 (만료일 기준과 통일)
        usableDate:
          ticket.usageDurationDays != null && ticket.dtFirstUse
            ? ticket.dtFirstUse
            : ticket.usableDate,
        expiryDate: ticket.expiredDate || null,
        usageDurationDays: ticket.usageDurationDays ?? null,
        isFirstUsePending:
          ticket.usageDurationDays != null && ticket.dtFirstUse == null,
        isCountBased: ticket.splitYn === "Y",
        // splitYn="N"(단일권)도 totalCount/remainCount가 의미 있으므로 보존하여 횟수 표기에 사용
        totalCount: ticket.totalCount ?? null,
        remainingCount: ticket.remainCount ?? null,
        cdProductType: ticket.cdProductType,
        status,
      };
    });
  }, [ticketList]);

  // 탭에 따라 필터링된 이용권 목록
  const filteredSubscriptions = useMemo(() => {
    if (tab === "사용 가능") {
      return subscriptions.filter((sub) => sub.status === "active");
    } else {
      // 사용 불가능: 만료됨 또는 사용완료
      return subscriptions.filter(
        (sub) => sub.status === "expired" || sub.status === "used"
      );
    }
  }, [subscriptions, tab]);

  if (isLoading) {
    return (
      <>
        <LayoutHeader title="이용권 조회" hasBackBtn hasFootPrint={false} />
        <div className="flex justify-center items-center flex-auto">
          <Loading visible={true} />
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader title="이용권 조회" hasBackBtn hasFootPrint={false} />
      <div className="flex flex-col flex-auto bg-[#F5F5F5] pb-24">
        <Tabs tabList={tabList} handleTab={handleTab} tab={tab}>
          <div className="px-4 pt-4 pb-6">
            {filteredSubscriptions.length === 0 ? (
              <div className="flex flex-col justify-center items-center py-20">
                <p className="mb-4 text-gray-500">
                  {tab === "사용 가능"
                    ? "사용 가능한 이용권이 없습니다."
                    : "사용 불가능한 이용권이 없습니다."}
                </p>
                <p className="text-sm text-gray-400">
                  {tab === "사용 가능"
                    ? "이용권을 구매한 후 여기에서 확인할 수 있습니다."
                    : "만료되거나 사용 완료된 이용권이 없습니다."}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {filteredSubscriptions.map((subscription) => (
                  <div
                    key={subscription.subscriptionSeq}
                    role="button"
                    tabIndex={0}
                    className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-colors cursor-pointer hover:border-orange-300"
                    onClick={() => {
                      navigate({
                        to: "/customer/layout/user-subscription-detail",
                        search: {
                          subscriptionSeq: subscription.subscriptionSeq,
                        },
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate({
                          to: "/customer/layout/user-subscription-detail",
                          search: {
                            subscriptionSeq: subscription.subscriptionSeq,
                          },
                        });
                      }
                    }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="mb-1 text-base font-semibold text-gray-800 truncate">
                          {subscription.planName}
                        </h3>
                        <p className="mb-2 text-sm text-gray-600 truncate">
                          {subscription.companyName}
                        </p>
                        <div className="flex flex-col gap-1 text-xs text-gray-500">
                          <div className="flex gap-2 justify-between items-center">
                            <span className="shrink-0">
                              구매일: {subscription.purchaseDate}
                            </span>
                            {typeof subscription.totalCount === "number" &&
                              typeof subscription.remainingCount === "number" &&
                              (subscription.cdProductType ===
                              "PRODUCT_TYPE_HOTEL" ? (
                                // 호텔은 박수 누적 차감 없이 1회 사용 시 전량 소진 — 박수만 표시
                                <span className="text-right shrink-0 whitespace-nowrap text-gray-500">
                                  {subscription.totalCount}박
                                </span>
                              ) : (
                                <span className="text-right shrink-0 whitespace-nowrap">
                                  <span className="text-gray-500">
                                    {Math.max(
                                      0,
                                      subscription.totalCount -
                                        subscription.remainingCount,
                                    )}
                                    회 사용
                                  </span>
                                  <span
                                    className={
                                      subscription.remainingCount === 0
                                        ? "ml-1 text-gray-400"
                                        : "ml-1 font-medium text-orange-500"
                                    }
                                  >
                                    ({subscription.remainingCount}회 남음)
                                  </span>
                                </span>
                              ))}
                          </div>
                          <div className="flex gap-2 justify-between items-center whitespace-nowrap">
                            {subscription.isFirstUsePending ? (
                              <span>
                                첫 이용일로부터 {subscription.usageDurationDays}
                                일 사용 가능
                              </span>
                            ) : (
                              <>
                                <span>시작일: {subscription.usableDate}</span>
                                <span>
                                  만료일:{" "}
                                  {subscription.expiryDate || "만료일 없음"}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        {subscription.status === "active" && (
                          <span className="px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded">
                            사용가능
                          </span>
                        )}
                        {subscription.status === "expired" && (
                          <span className="px-2 py-1 text-xs font-medium text-[#666666] bg-[#F5F5F5] rounded">
                            만료됨
                          </span>
                        )}
                        {subscription.status === "used" && (
                          <span className="px-2 py-1 text-xs font-medium text-[#666666] bg-[#F5F5F5] rounded">
                            사용완료
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Tabs>
      </div>
    </>
  );
}
